
import Navbar from '../../../components/feature/Navbar';
import { useScrollAnimation, getAnimationClass } from '../../../hooks/useScrollAnimation';

function AnimatedSection({ children, animation = 'slide-up' as 'fade-in' | 'slide-up' | 'slide-left' | 'slide-right' | 'scale-in', className = '' }: { children: React.ReactNode; animation?: 'fade-in' | 'slide-up' | 'slide-left' | 'slide-right' | 'scale-in'; className?: string }) {
  const { ref, isVisible } = useScrollAnimation();
  return (
    <div ref={ref} className={`${getAnimationClass(isVisible, animation)} ${className}`}>
      {children}
    </div>
  );
}

export default function Hero() {
  return (
    <div className="relative min-h-screen overflow-hidden">
      {/* Background Image */}
      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=abstract%20dark%20emerald%20green%20and%20black%20gradient%20background%20with%20subtle%20geometric%20patterns%20and%20soft%20light%20rays%20futuristic%20medical%20technology%20aesthetic%20with%20bokeh%20light%20effects%20deep%20dark%20tones%20elegant%20sophisticated%20atmosphere&width=1920&height=1080&seq=hero-bg-main-1&orientation=landscape"
          alt="Anamnesy Soft background"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/50 to-black/70"></div>
      </div>

      {/* Navigation */}
      <Navbar />

      {/* Hero Content */}
      <div className="relative pt-36 pb-24 px-6 lg:px-12 w-full">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Left Content */}
            <AnimatedSection animation="slide-left">
              <div className="space-y-8 w-full">
                <div className="inline-flex items-center space-x-2 px-4 py-1.5 bg-emerald-500/20 border border-emerald-400/30 rounded-full">
                  <span className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse"></span>
                  <span className="text-xs font-semibold text-emerald-300 tracking-wide uppercase">Software Oftalmológico SaaS</span>
                </div>

                <h2 className="text-4xl lg:text-6xl font-bold text-white leading-tight">
                  Control oftalmológico
                  <span className="font-display italic text-emerald-400"> inteligente</span>
                  <br />y preciso
                </h2>

                <p className="text-base lg:text-lg text-white/80 leading-relaxed max-w-lg">
                  Anamnesy Soft revoluciona la gestión clínica oftalmológica con historiales digitales, diagnósticos asistidos, módulo de óptica integrado y seguimiento automatizado de pacientes. Todo en una sola plataforma.
                </p>

                <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-3 sm:space-y-0 sm:space-x-4">
                  <a href="https://anamnesysoft.com/" target="_blank" rel="noopener noreferrer" className="px-8 py-3.5 bg-emerald-500 text-white text-sm font-semibold rounded-lg hover:bg-emerald-600 transition-all hover:shadow-lg hover:shadow-emerald-500/25 whitespace-nowrap cursor-pointer flex items-center space-x-2">
                    <i className="ri-login-circle-line text-base"></i>
                    <span>Registrarse / Ingresar</span>
                  </a>
                  <a href="#producto" className="px-8 py-3.5 bg-white/10 text-white text-sm font-semibold rounded-lg border border-white/20 hover:bg-white/20 transition-all whitespace-nowrap cursor-pointer flex items-center space-x-2">
                    <i className="ri-play-circle-line text-base"></i>
                    <span>Ver Producto</span>
                  </a>
                </div>

                {/* Stats Row */}
                <div className="flex items-center space-x-8 pt-4">
                  <div>
                    <p className="text-3xl font-bold text-white">500+</p>
                    <p className="text-xs text-white/60 mt-1">Profesionales activos</p>
                  </div>
                  <div className="w-px h-12 bg-white/20"></div>
                  <div>
                    <p className="text-3xl font-bold text-white">50K+</p>
                    <p className="text-xs text-white/60 mt-1">Pacientes gestionados</p>
                  </div>
                  <div className="w-px h-12 bg-white/20"></div>
                  <div>
                    <p className="text-3xl font-bold text-white">99.9%</p>
                    <p className="text-xs text-white/60 mt-1">Uptime garantizado</p>
                  </div>
                </div>
              </div>
            </AnimatedSection>

            {/* Right Content - Dashboard Preview */}
            <AnimatedSection animation="slide-right" className="hidden lg:block">
              <div className="relative">
                <div className="bg-white/10 backdrop-blur-xl rounded-2xl p-6 border border-white/20 shadow-2xl">
                  <div className="space-y-4">
                    <div className="flex items-center justify-between pb-3 border-b border-white/10">
                      <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 bg-emerald-500 rounded-lg flex items-center justify-center">
                          <i className="ri-eye-line text-white text-sm"></i>
                        </div>
                        <h3 className="text-sm font-semibold text-white">Panel Anamnesy Soft</h3>
                      </div>
                      <div className="flex items-center space-x-1">
                        <div className="w-2.5 h-2.5 rounded-full bg-emerald-400"></div>
                        <div className="w-2.5 h-2.5 rounded-full bg-yellow-400"></div>
                        <div className="w-2.5 h-2.5 rounded-full bg-red-400"></div>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div className="bg-white/10 rounded-xl p-4">
                        <div className="flex items-center space-x-2 mb-2">
                          <i className="ri-user-heart-line text-emerald-400 text-sm"></i>
                          <span className="text-[11px] text-white/70">Pacientes Hoy</span>
                        </div>
                        <p className="text-2xl font-bold text-white">24</p>
                        <p className="text-[10px] text-emerald-400 mt-1">+12% vs ayer</p>
                      </div>
                      <div className="bg-white/10 rounded-xl p-4">
                        <div className="flex items-center space-x-2 mb-2">
                          <i className="ri-glasses-line text-emerald-400 text-sm"></i>
                          <span className="text-[11px] text-white/70">Ventas Óptica</span>
                        </div>
                        <p className="text-2xl font-bold text-white">$2.4K</p>
                        <p className="text-[10px] text-emerald-400 mt-1">+8% esta semana</p>
                      </div>
                    </div>

                    <div className="space-y-2">
                      {[
                        { name: 'María García', type: 'Examen de Retina', time: '09:00', status: 'Completado', statusColor: 'bg-emerald-500' },
                        { name: 'Carlos López', type: 'Control Glaucoma', time: '10:30', status: 'En curso', statusColor: 'bg-yellow-500' },
                        { name: 'Ana Martínez', type: 'Agudeza Visual', time: '11:15', status: 'Esperando', statusColor: 'bg-white/40' },
                        { name: 'Roberto Díaz', type: 'Lentes de Contacto', time: '12:00', status: 'Programado', statusColor: 'bg-white/20' },
                      ].map((patient, index) => (
                        <div key={index} className="flex items-center space-x-3 p-3 rounded-lg bg-white/5 hover:bg-white/10 transition-colors cursor-pointer">
                          <div className={`w-2 h-2 rounded-full ${patient.statusColor} flex-shrink-0`}></div>
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-semibold text-white truncate">{patient.name}</p>
                            <p className="text-[10px] text-white/50">{patient.type}</p>
                          </div>
                          <span className="text-[10px] text-white/40 whitespace-nowrap">{patient.time}</span>
                        </div>
                      ))}
                    </div>

                    <div className="bg-white/5 rounded-xl p-4">
                      <p className="text-[11px] text-white/60 mb-3">Diagnósticos esta semana</p>
                      <div className="flex items-end space-x-2 h-16">
                        {[40, 65, 45, 80, 55, 70, 90].map((h, i) => (
                          <div key={i} className="flex-1 bg-emerald-500/40 rounded-t-sm hover:bg-emerald-500/70 transition-colors cursor-pointer" style={{ height: `${h}%` }}></div>
                        ))}
                      </div>
                      <div className="flex justify-between mt-2">
                        {['L', 'M', 'X', 'J', 'V', 'S', 'D'].map((d, i) => (
                          <span key={i} className="text-[9px] text-white/30 flex-1 text-center">{d}</span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="absolute -bottom-4 -left-4 bg-white rounded-xl p-4 shadow-2xl border border-gray-100">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <i className="ri-shield-check-line text-emerald-600 text-lg"></i>
                    </div>
                    <div>
                      <p className="text-xs font-bold text-gray-900">HIPAA Compliant</p>
                      <p className="text-[10px] text-gray-500">Datos 100% seguros</p>
                    </div>
                  </div>
                </div>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center space-y-2 animate-bounce">
        <span className="text-[10px] text-white/40 uppercase tracking-widest">Descubre más</span>
        <i className="ri-arrow-down-line text-white/40 text-lg"></i>
      </div>
    </div>
  );
}
